import { useEffect, useMemo, useRef } from 'react'
import maplibregl from 'maplibre-gl'
import Supercluster from 'supercluster'
import type { PointFeature, ClusterProperties } from 'supercluster'

export interface ClusterPoint {
	id: string
	longitude: number
	latitude: number
	properties?: Record<string, unknown>
}

export interface ClusterMarkersProps {
	map: maplibregl.Map | null
	isMapLoaded: boolean
	points: ClusterPoint[]
	renderLeaf: (point: ClusterPoint, onClick: () => void) => HTMLElement
	onLeafClick?: (pointId: string) => void
	clusterRadius?: number
	maxClusterZoom?: number
}

type LeafProperties = { id: string } & Record<string, unknown>

function buildClusterElement(count: number, onClick: () => void): HTMLDivElement {
	const size = count < 10 ? 36 : count < 50 ? 44 : 52
	const bg = count < 10 ? '#0080FE' : count < 50 ? '#F97316' : '#E53E3E'
	const el = document.createElement('div')
	el.style.cssText = `
		width:${size}px;height:${size}px;border-radius:50%;
		background:${bg};color:#ffffff;
		display:flex;align-items:center;justify-content:center;
		font-weight:700;font-size:${count < 100 ? 14 : 12}px;
		font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
		border:3px solid rgba(255,255,255,0.9);
		box-shadow:0 2px 8px rgba(0,0,0,0.25);
		cursor:pointer;
		transition:transform 150ms ease;
	`
	el.textContent = count > 999 ? '999+' : String(count)
	el.addEventListener('mouseenter', () => {
		el.style.transform = 'scale(1.1)'
	})
	el.addEventListener('mouseleave', () => {
		el.style.transform = 'scale(1)'
	})
	el.addEventListener('click', onClick)
	return el
}

export function ClusterMarkers({
	map,
	isMapLoaded,
	points,
	renderLeaf,
	onLeafClick,
	clusterRadius = 50,
	maxClusterZoom = 16,
}: ClusterMarkersProps) {
	const indexRef = useRef<Supercluster<LeafProperties> | null>(null)
	const markersRef = useRef<maplibregl.Marker[]>([])

	const features = useMemo<Array<PointFeature<LeafProperties>>>(
		() =>
			points.map((p) => ({
				type: 'Feature',
				geometry: { type: 'Point', coordinates: [p.longitude, p.latitude] },
				properties: { id: p.id, ...p.properties },
			})),
		[points],
	)

	useEffect(() => {
		const index = new Supercluster<LeafProperties>({
			radius: clusterRadius,
			maxZoom: maxClusterZoom,
		})
		index.load(features)
		indexRef.current = index
	}, [features, clusterRadius, maxClusterZoom])

	useEffect(() => {
		if (!map || !isMapLoaded) return

		const render = () => {
			const index = indexRef.current
			if (!index) return

			markersRef.current.forEach((m) => m.remove())
			markersRef.current = []

			const bounds = map.getBounds()
			const bbox: [number, number, number, number] = [
				bounds.getWest(),
				bounds.getSouth(),
				bounds.getEast(),
				bounds.getNorth(),
			]
			const zoom = Math.floor(map.getZoom())
			const clusters = index.getClusters(bbox, zoom)

			for (const feature of clusters) {
				const [lng, lat] = feature.geometry.coordinates
				const props = feature.properties as ClusterProperties | LeafProperties

				let el: HTMLElement
				if ('cluster' in props && props.cluster) {
					const clusterProps = props as ClusterProperties
					const clusterId = clusterProps.cluster_id
					const count = clusterProps.point_count
					el = buildClusterElement(count, () => {
						const expansion = index.getClusterExpansionZoom(clusterId)
						map.flyTo({ center: [lng, lat], zoom: expansion })
					})
				} else {
					const leaf = props as LeafProperties
					const point: ClusterPoint = {
						id: leaf.id,
						longitude: lng,
						latitude: lat,
						properties: leaf,
					}
					el = renderLeaf(point, () => onLeafClick?.(leaf.id))
				}

				const marker = new maplibregl.Marker({ element: el }).setLngLat([lng, lat]).addTo(map)
				markersRef.current.push(marker)
			}
		}

		render()
		map.on('moveend', render)
		map.on('zoomend', render)

		return () => {
			map.off('moveend', render)
			map.off('zoomend', render)
			markersRef.current.forEach((m) => m.remove())
			markersRef.current = []
		}
	}, [map, isMapLoaded, features, renderLeaf, onLeafClick])

	return null
}
